import { VERSION_DELETE_REFUSALS, type VersionDeleteRefusal } from '@noconflict/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteVersion, scriptVersionKeys } from '../../../entities/script-version';
import { ApiRequestError } from '../../../shared/api/http';
import { supabase } from '../../../shared/api/supabase';
import { Button } from '../../../shared/ui/button';
import { ConfirmDialog } from '../../../shared/ui/confirm-dialog';
import { toast } from '../../../shared/ui/sonner';

const isRefusal = (code: string): code is VersionDeleteRefusal =>
  (VERSION_DELETE_REFUSALS as readonly string[]).includes(code);

/** Deletes a version through the worker and refreshes the card. */
function useDeleteVersion(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (versionId: string) => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('forbidden');
      await deleteVersion(token, projectId, versionId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: scriptVersionKeys.all }),
  });
}

/**
 * **Delete** on the newest version, behind a confirmation that says what goes with it. A refusal (reviewed in
 * the meantime, a run started) is said in the dialog.
 */
export function DeleteVersionButton({
  projectId,
  versionId,
  number,
}: {
  projectId: string;
  versionId: string;
  number: number;
}) {
  const { t } = useTranslation('project');
  const remove = useDeleteVersion(projectId);
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        aria-label={t('versions.delete.actionLabel', { number })}
        onClick={() => setOpen(true)}
      >
        <Trash2Icon aria-hidden="true" />
        {t('versions.delete.action')}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={t('versions.delete.title', { number })}
        description={t('versions.delete.body')}
        confirmLabel={t('versions.delete.confirm')}
        cancelLabel={t('versions.delete.keep')}
        errorMessage={(e) =>
          e instanceof ApiRequestError && isRefusal(e.code)
            ? t(`versions.delete.errors.${e.code}`)
            : t('versions.delete.errors.generic')
        }
        onConfirm={async () => {
          await remove.mutateAsync(versionId);
          toast.success(t('versions.delete.done'));
        }}
      />
    </>
  );
}
